import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, Library, Loader2, Plus, Search, Trash2, X } from "lucide-react";
import Layout from "@/components/Layout";
import { EmptyState, LoadingPanel } from "@/components/Bits";
import { DatePicker } from "@/components/Calendar";
import { Pill } from "@/components/Music";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/Confirm";
import { api, apiError, formatDate } from "@/lib/api";
import { useRemembered } from "@/lib/useRemembered";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import { useTenant } from "@/context/TenantContext";

import { cn } from "@/lib/utils";
import { ListInput } from "@/pages/TrackEditor";

/** Fetch every album's page in the background, so opening one paints at once (see lib/cache.js). */
export async function warmAlbums(tenantId, albums) {
  for (const a of albums || []) {
    const key = cacheKey(`album:${a.album_id}`, tenantId);
    if (cacheGet(key) != null) continue;
    try {
      const { data } = await api.get(`/albums/${a.album_id}`);
      cacheSet(key, data);
    } catch {
      /* the page loads it itself */
    }
  }
}

const inputCls = "w-full h-10 rounded-lg border border-line bg-card px-3 text-[13.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

/** Albums — an album, EP, riddim or re-release, made once. Its songs show it on their track pages. */
export default function Albums() {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const [data, setData] = useRemembered("albums");
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api
      .get("/albums")
      .then(({ data: d }) => {
        setData(d);
        warmAlbums(tenantId, d.albums);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [setData, tenantId]);
  useEffect(() => {
    load();
  }, [load]);

  const create = async (s) => {
    setBusy(s?.key || "new");
    try {
      const body = s ? { name: s.name, date: s.date, upc: s.upc, track_ids: s.tracks.map((t) => t.track_id) } : { name: "" };
      const { data: a } = await api.post("/albums", body);
      if (s) {
        toast.success(`${s.name} created with ${a.tracks} track${a.tracks === 1 ? "" : "s"} — open it to check the date and UPC`);
        load();
      } else {
        cacheSet(cacheKey(`album:${a.album_id}`, tenantId), { ...a, track_list: [] });
        navigate(`/albums/${a.album_id}`);
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy("");
    }
  };
  const dismiss = async (s) => {
    setData((d) => ({ ...d, suggestions: d.suggestions.filter((x) => x.key !== s.key) }));
    try {
      await api.post(`/albums/suggestions/${encodeURIComponent(s.key)}/dismiss`);
    } catch (err) {
      toast.error(apiError(err));
      load();
    }
  };

  const albums = data?.albums || [];
  const sugs = data?.suggestions || [];
  const shown = showAll ? sugs : sugs.slice(0, 8);

  return (
    <Layout
      title="Albums"
      subtitle={data ? `${albums.length} album${albums.length === 1 ? "" : "s"} · albums, EPs, riddims and re-releases` : "Loading…"}
      action={
        <Button className="bg-brand hover:bg-brand-hover" onClick={() => create(null)} disabled={busy === "new"}>
          <Plus className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">New album</span>
        </Button>
      }
    >
      {!data ? (
        <LoadingPanel rows={5} />
      ) : (
        <>
          {albums.length ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 mb-6">
              {albums.map((a) => (
                <Link key={a.album_id} to={`/albums/${a.album_id}`} className="panel p-4 hover:border-brand-ring transition-colors flex flex-col gap-1">
                  <div className="font-display text-[17px] font-semibold leading-tight">{a.name || "Untitled album"}</div>
                  <div className="text-[12.5px] text-muted2">
                    {a.date ? formatDate(a.date) : "No date"} · {a.tracks} track{a.tracks === 1 ? "" : "s"}
                  </div>
                  <div className="text-[12px] text-muted2 truncate">
                    {a.titles.join(", ")}
                    {a.tracks > a.titles.length ? "…" : ""}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="panel mb-6">
              <EmptyState icon={Library} title="No albums yet" body="Start from the suggestions below — one click each — or make one with New album." />
            </div>
          )}

          {sugs.length > 0 && (
            <>
              <div className="rounded-xl bg-brand-light/60 px-4 py-3 mb-3 text-[13px] flex flex-wrap gap-x-2">
                <b>
                  {sugs.length} suggestion{sugs.length === 1 ? "" : "s"}
                </b>
                <span className="text-muted2">from the Riddim / Album column in your catalog. Create the ones that are real albums; dismiss the rest.</span>
              </div>
              <div className="space-y-2">
                {shown.map((s) => (
                  <div key={s.key} className={cn("rounded-xl border border-dashed p-3 flex items-center gap-3 flex-wrap bg-card", s.odd ? "border-gold bg-gold-light/50" : "border-line")}>
                    <div className="min-w-0 flex-1 basis-[240px]">
                      <div className="text-[13.5px]">
                        <b>{s.name}</b>
                        <span className="text-muted2 text-[12.5px]">
                          {" "}
                          · {s.tracks.length} track{s.tracks.length === 1 ? "" : "s"}
                          {s.date ? ` · ${formatDate(s.date)}` : ""}
                          {s.upc ? ` · shared UPC ${s.upc}` : ""}
                        </span>
                      </div>
                      <div className="text-[12px] text-muted2 truncate">
                        {s.tracks
                          .slice(0, 4)
                          .map((t) => t.title)
                          .join(", ")}
                        {s.tracks.length > 4 ? ` and ${s.tracks.length - 4} more` : ""}
                      </div>
                      {s.spellings.length > 1 && <div className="text-[12px] text-sec-pub">Spelled {s.spellings.map((x) => `“${x}”`).join(" and ")} in your catalog — combined into one.</div>}
                      {s.odd && <div className="text-[12px] text-gold">Doesn't look like an album name — probably a note in that column.</div>}
                    </div>
                    <Button size="sm" disabled={busy === s.key} className={s.odd ? "" : "bg-brand hover:bg-brand-hover"} variant={s.odd ? "outline" : "default"} onClick={() => create(s)}>
                      {busy === s.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Create album"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => dismiss(s)}>
                      Dismiss
                    </Button>
                  </div>
                ))}
              </div>
              {sugs.length > 8 && !showAll && (
                <button onClick={() => setShowAll(true)} className="mt-3 text-[13px] font-semibold text-brand">
                  Show all {sugs.length} suggestions
                </button>
              )}
            </>
          )}
        </>
      )}
    </Layout>
  );
}

/** One album: its details at the top, then its tracks — add by search, remove with ×. */
export function AlbumPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { tenantId } = useTenant();
  const CK = cacheKey(`album:${id}`, tenantId);
  const seed = cacheGet(CK) || null;
  const toForm = (d) => (d ? { name: d.name, date: d.date, upc: d.upc, label: d.label, distributor: d.distributor || "" } : null);
  const [a, setA] = useState(seed);
  const [form, setForm] = useState(() => toForm(seed));
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);
  // the same label / distributor suggestions as the track page
  const [values, setValues] = useState({ label: [], distributor: [] });
  useEffect(() => {
    api.get("/values").then(({ data }) => setValues(data)).catch(() => {});
  }, []);
  const [sel, setSel] = useState(() => new Set());
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyOpts, setCopyOpts] = useState({ date: true, upc: true, label: true, distributor: true, released: true });
  const [replace, setReplace] = useState(false);
  const [copying, setCopying] = useState(false);
  const req = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/albums/${id}`);
      setA(data);
      cacheSet(CK, data);
      setForm((f) => f || toForm(data));
    } catch (err) {
      toast.error(apiError(err));
      if (err?.response?.status === 404) navigate("/albums", { replace: true });
    }
  }, [id, navigate, CK]);
  useEffect(() => {
    // show what we remember straight away, then refresh quietly
    const c = cacheGet(CK) || null;
    setA(c);
    setForm(toForm(c));
    load();
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return undefined;
    }
    const n = ++req.current;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/tracks", { params: { q } });
        if (n === req.current) setResults(data.tracks.filter((x) => !a?.track_list.some((y) => y.track_id === x.track_id)).slice(0, 6));
      } catch {
        /* the search just shows nothing */
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, a]);

  const missing = a ? a.track_list.filter((t) => !t.release_date).length : 0;
  const dirty = a && form && ["name", "date", "upc", "label", "distributor"].some((k) => (form[k] || "") !== (a[k] || ""));
  // songs whose own date / UPC come from this album follow it when it changes
  const following = a ? a.track_list.filter((t) => t.album_only && t.from_album_id === id) : [];
  const releaseChanged = a && form && ((form.date || "") !== (a.date || "") || (form.upc || "").replace(/\D/g, "") !== (a.upc || "").replace(/\D/g, ""));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/albums/${id}`, form);
      toast.success(data.tracks_updated ? `Album saved — ${data.tracks_updated} song${data.tracks_updated === 1 ? "" : "s"} that take their date and UPC from it updated too` : "Album saved — its tracks show the change");
      data.tracks_updated && a.track_list.forEach((t) => cacheSet(cacheKey(`track:${t.track_id}`, tenantId), null));
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };
  const add = async (t) => {
    try {
      await api.post(`/albums/${id}/tracks`, { track_id: t.track_id });
      setQ("");
      toast.success(`${t.title} added — its track page now shows this album`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const removeTrack = async (t) => {
    try {
      await api.delete(`/albums/${id}/tracks/${t.track_id}`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const toggle = (tid) =>
    setSel((s) => {
      const n = new Set(s);
      n.has(tid) ? n.delete(tid) : n.add(tid);
      return n;
    });
  const openCopy = () => {
    if (!sel.size) setSel(new Set(a.track_list.map((t) => t.track_id)));
    setCopyOpen(true);
  };
  const plan = (a && copyOpen ? a.track_list.filter((t) => sel.has(t.track_id)) : []).map((t) => ({ t, c: copyChanges(t, a, copyOpts, replace) }));
  const touched = plan.filter((p) => p.c.length);
  const applyCopy = async () => {
    setCopying(true);
    try {
      const { data } = await api.post(`/albums/${id}/copy`, { track_ids: touched.map((p) => p.t.track_id), fields: COPY_FIELDS.map((f) => f.k).filter((k) => copyOpts[k]), replace });
      touched.forEach((p) => cacheSet(cacheKey(`track:${p.t.track_id}`, tenantId), null));
      cacheSet(cacheKey("catalog", tenantId), null);
      toast.success(`Updated ${data.changed} track${data.changed === 1 ? "" : "s"} from the album`);
      setCopyOpen(false);
      setSel(new Set());
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setCopying(false);
    }
  };
  const remove = async () => {
    if (!(await confirm({ title: `Delete ${a.name || "this album"}?`, body: "Only the album goes — its tracks stay in your catalog.", confirmText: "Delete album", destructive: true }))) return;
    try {
      await api.delete(`/albums/${id}`);
      cacheSet(CK, null);
      toast.success("Album deleted");
      navigate("/albums");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Layout
      title={a ? a.name || "New album" : "Album"}
      subtitle={a ? `${a.track_list.length} track${a.track_list.length === 1 ? "" : "s"} · ${a.date ? formatDate(a.date) : "no date yet"}` : "Loading…"}
      action={
        a && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={remove} className="text-stock-out">
              <Trash2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Delete</span>
            </Button>
            <Button className="bg-brand hover:bg-brand-hover" onClick={save} disabled={!dirty || saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        )
      }
    >
      <Link to="/albums" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> Albums
      </Link>
      {!a || !form ? (
        <LoadingPanel rows={5} />
      ) : (
        <section className="panel overflow-hidden">
          <div className="px-5 py-3.5 border-b border-line flex items-center gap-2.5 flex-wrap">
            <span className="font-mono text-[10.5px] font-semibold tracking-[0.12em] uppercase rounded-full px-2.5 py-0.5 bg-sec-release-light text-sec-release">● Album</span>
            <span className="text-[13px] text-muted2">Its songs show this album on their track pages</span>
          </div>
          <div className="p-5 grid md:grid-cols-2 xl:grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-4">
            <label className="block">
              <span className="block text-[12px] font-semibold mb-1.5">Name</span>
              <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. La Vida Loca Riddim (2023 re-release)" autoFocus={!a.name} />
            </label>
            <div>
              <span className="block text-[12px] font-semibold mb-1.5">Release date</span>
              <DatePicker value={form.date} onChange={(v) => setForm({ ...form, date: v || "" })} ariaLabel="Release date" />
            </div>
            <label className="block">
              <span className="block text-[12px] font-semibold mb-1.5">UPC</span>
              <input className={cn(inputCls, "font-mono")} value={form.upc} onChange={(e) => setForm({ ...form, upc: e.target.value })} inputMode="numeric" placeholder="12–13 digits" />
            </label>
            <label className="block">
              <span className="block text-[12px] font-semibold mb-1.5">Label</span>
              <ListInput id="album-labels" value={form.label} onChange={(v) => setForm({ ...form, label: v })} options={(values.label || []).map((v) => v.value)} />
            </label>
            <label className="block">
              <span className="block text-[12px] font-semibold mb-1.5">Distributor</span>
              <ListInput id="album-dists" value={form.distributor} onChange={(v) => setForm({ ...form, distributor: v })} options={(values.distributor || []).map((v) => v.value)} placeholder="e.g. Tunecore" />
            </label>
          </div>
          {dirty && <div className="px-5 -mt-2 pb-3 text-[12px] text-gold">Unsaved changes — click Save.</div>}
          {releaseChanged && following.length > 0 && (
            <div className="mx-5 mb-4 -mt-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 text-[12.5px]">
              <b>
                {following.length} song{following.length === 1 ? " takes its" : "s take their"} date and UPC from this album.
              </b>{" "}
              Saving updates {following.length === 1 ? "it" : "them"} too. Songs with their own single release aren't touched.
            </div>
          )}
          {a.track_list.length > 0 && (
            <div className="px-5 py-2.5 border-t border-line bg-surface/60 flex items-center gap-3 flex-wrap">
              <span className="text-[13px] flex-1 min-w-0">
                {sel.size ? (
                  <>
                    <b>{sel.size}</b> selected
                  </>
                ) : missing ? (
                  <>
                    <b>{missing}</b> of {a.track_list.length} tracks have no release date.
                  </>
                ) : (
                  "Every track has a release date."
                )}
              </span>
              {!copyOpen && (
                <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={openCopy} disabled={dirty} title={dirty ? "Save the album first" : undefined}>
                  <Copy className="w-4 h-4 mr-1.5" /> Copy album info to {sel.size ? `${sel.size} selected` : "tracks"}
                </Button>
              )}
            </div>
          )}
          {copyOpen && (
            <CopyPanel
              album={a}
              picked={plan.length}
              plan={plan}
              touched={touched}
              opts={copyOpts}
              setOpts={setCopyOpts}
              replace={replace}
              setReplace={setReplace}
              busy={copying}
              onSelectAll={() => setSel(new Set(a.track_list.map((t) => t.track_id)))}
              onApply={applyCopy}
              onClose={() => setCopyOpen(false)}
            />
          )}
          <div className="overflow-x-auto border-t border-line">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="bg-surface/70 border-b border-line">
                  <th className="px-4 py-2.5 w-10">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-brand align-middle"
                      aria-label="Select all tracks"
                      checked={a.track_list.length > 0 && sel.size === a.track_list.length}
                      ref={(el) => el && (el.indeterminate = sel.size > 0 && sel.size < a.track_list.length)}
                      onChange={(e) => setSel(e.target.checked ? new Set(a.track_list.map((t) => t.track_id)) : new Set())}
                    />
                  </th>
                  {["#", "Track", "Release date", "UPC", "Its own release", ""].map((h) => (
                    <th key={h || "x"} className="t-head text-left px-4 py-2.5 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {a.track_list.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-[13px] text-muted2">
                      No tracks yet — add them below.
                    </td>
                  </tr>
                ) : (
                  a.track_list.map((t, i) => (
                    <tr
                      key={t.track_id}
                      className={cn("t-row cursor-pointer", sel.has(t.track_id) && "bg-brand-light/50")}
                      onClick={(e) => {
                        if (!e.target.closest("button, input, [data-no-open]")) navigate(`/catalog/${t.track_id}?album=${id}`);
                      }}
                    >
                      <td className="t-cell w-10" data-no-open onClick={() => toggle(t.track_id)}>
                        <input type="checkbox" className="w-4 h-4 accent-brand align-middle" checked={sel.has(t.track_id)} onChange={() => {}} aria-label={`Select ${t.title}`} />
                      </td>
                      <td className="t-cell text-muted2 w-12">{i + 1}</td>
                      <td className="t-cell">
                        <div className="font-semibold">{t.title}</div>
                        <div className="text-[12px] text-muted2">{t.artist || "—"}</div>
                      </td>
                      <td className="t-cell text-[12.5px] whitespace-nowrap">
                        {!t.release_date ? (
                          <span className="text-muted2">—</span>
                        ) : t.from_album ? (
                          <>
                            <span className="text-muted2">{formatDate(t.release_date)}</span>
                            <span className="block text-[11.5px] text-muted2">from {t.from_album}</span>
                          </>
                        ) : (
                          formatDate(t.release_date)
                        )}
                      </td>
                      <td className="t-cell font-mono text-[12px]">
                        {!t.upc ? (
                          <span className="text-muted2">—</span>
                        ) : t.from_album ? (
                          <>
                            <span className="text-muted2">{t.upc}</span>
                            <span className="block font-sans text-[11.5px] text-muted2">from {t.from_album}</span>
                          </>
                        ) : (
                          t.upc
                        )}
                      </td>
                      <td className="t-cell text-[12.5px]">
                        {t.album_only ? <span className="text-muted2">{t.from_album ? `Album only · first on ${t.from_album}` : "Album only"}</span> : t.release_date ? `Single · ${formatDate(t.release_date)}` : <span className="text-muted2">—</span>}
                      </td>
                      <td className="t-cell text-right">
                        <button onClick={() => removeTrack(t)} title="Take off this album" aria-label={`Take ${t.title} off this album`} className="p-1.5 rounded-lg text-muted2 hover:bg-surface hover:text-ink">
                          <X className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="p-5 border-t border-line">
            <span className="block text-[12px] font-semibold mb-1.5">Add tracks</span>
            <label className="relative block">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
              <input className={cn(inputCls, "pl-9")} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your catalog — title, artist, ISRC…" aria-label="Search your catalog" />
            </label>
            {results.length > 0 && (
              <div className="mt-1.5 rounded-lg border border-line divide-y divide-line overflow-hidden">
                {results.map((t) => (
                  <button key={t.track_id} type="button" onClick={() => add(t)} className="w-full flex items-center justify-between gap-3 text-left px-3 py-2 bg-card hover:bg-brand-light/50">
                    <span className="min-w-0">
                      <span className="text-[13px] font-semibold">{t.title}</span> <span className="text-[12.5px] text-muted2">{t.artist}</span>
                      <span className="block font-mono text-[11px] text-muted2">
                        {t.isrc || "no ISRC"}
                        {t.riddim ? ` · ${t.riddim}` : ""}
                      </span>
                    </span>
                    <Pill tone="brand">Add</Pill>
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </Layout>
  );
}

const COPY_FIELDS = [
  { k: "date", key: "release_date", label: "Release date", show: (a) => (a.date ? formatDate(a.date) : "album has no date") },
  { k: "upc", key: "upc", label: "UPC", show: (a) => a.upc || "album has no UPC" },
  { k: "label", key: "label", label: "Label", show: (a) => a.label || "album has no label" },
  { k: "distributor", key: "distributor", label: "Distributor", show: (a) => a.distributor || "album has no distributor" },
  { k: "released", key: "released", label: "Released", show: () => "set to “Yes”" },
];
const albumValue = (a, k) => (k === "date" ? a.date : k === "released" ? "Yes" : a[k]) || "";
const digits = (s) => (s || "").replace(/\D/g, "");

/** What copying the album's info onto one track would change — the server applies the same rules. */
function copyChanges(t, a, opts, replace) {
  const out = [];
  for (const f of COPY_FIELDS) {
    const to = albumValue(a, f.k);
    if (!opts[f.k] || !to) continue;
    const from = t[f.key] || "";
    if (f.k === "upc" ? digits(from) === digits(to) : from === to) continue;
    if (from && !replace) continue;
    out.push({ ...f, from, to });
  }
  return out;
}

function CopyPanel({ album, picked, plan, touched, opts, setOpts, replace, setReplace, busy, onSelectAll, onApply, onClose }) {
  const count = (k) => touched.filter((p) => p.c.some((c) => c.k === k)).length;
  const kept = (t) => !replace && COPY_FIELDS.some((f) => opts[f.k] && albumValue(album, f.k) && t[f.key] && t[f.key] !== albumValue(album, f.k));
  const show = (c, v) => (c.k === "date" ? formatDate(v) : v);
  return (
    <div className="px-5 py-4 border-t border-line">
      <div className="rounded-xl border border-brand bg-card p-4 flex flex-col gap-3 shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-display text-[19px] font-semibold">
            Copy album info to {picked} track{picked === 1 ? "" : "s"}
          </h2>
          <span className="text-[12.5px] text-muted2">
            {picked ? "Tick or untick songs in the list below · " : "Tick songs in the list below, or "}
            <button type="button" onClick={onSelectAll} className="font-semibold text-brand underline">
              select all {album.track_list.length}
            </button>
          </span>
        </div>
        <div>
          <div className="text-[12.5px] font-semibold mb-1.5">What to copy</div>
          <div className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(190px,1fr))]">
            {COPY_FIELDS.map((f) => {
              const off = !albumValue(album, f.k);
              const on = opts[f.k] && !off;
              return (
                <label key={f.k} className={cn("flex gap-2.5 items-start rounded-lg border px-3 py-2.5", on ? "border-brand bg-brand-light" : "border-line", off ? "opacity-50" : "cursor-pointer")}>
                  <input type="checkbox" className="w-4 h-4 mt-0.5 accent-brand" checked={on} disabled={off} onChange={(e) => setOpts({ ...opts, [f.k]: e.target.checked })} />
                  <span className="text-[13px]">
                    <b>{f.label}</b>
                    <span className={cn("block text-[11.5px] text-muted2", f.k === "upc" && album.upc && "font-mono")}>{f.show(album)}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className="text-[12.5px] font-semibold">If a track already has something</span>
          <div className="inline-flex gap-0.5 p-0.5 rounded-lg border border-line bg-surface" role="group">
            {[
              [false, "Only fill in blanks"],
              [true, "Replace it"],
            ].map(([v, l]) => (
              <button key={l} type="button" aria-pressed={replace === v} onClick={() => setReplace(v)} className={cn("h-8 px-3 rounded-md text-[12.5px] font-semibold", replace === v ? "bg-card text-ink shadow-sm" : "text-muted2")}>
                {l}
              </button>
            ))}
          </div>
        </div>
        {picked > 0 && (
          <>
            <div className="overflow-x-auto rounded-lg border border-line">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-line">
                    <th className="t-head text-left px-3 py-2 font-medium">Track</th>
                    <th className="t-head text-left px-3 py-2 font-medium">What changes</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.map(({ t, c }) => (
                    <tr key={t.track_id} className="border-t border-line first:border-t-0">
                      <td className="px-3 py-2 text-[12.5px] font-semibold align-top w-[34%]">{t.title}</td>
                      <td className="px-3 py-2 text-[12.5px]">
                        {c.length ? (
                          c.map((x, i) => (
                            <span key={x.k}>
                              {i > 0 && " · "}
                              {x.label}: {x.from && <span className="text-muted2 line-through">{show(x, x.from)}</span>}
                              {x.from && " → "}
                              <span className="text-stock-in font-semibold">{show(x, x.to)}</span>
                            </span>
                          ))
                        ) : (
                          <span className="text-muted2">Nothing — already matches{kept(t) ? " or has its own (kept)" : ""}</span>
                        )}
                        {c.length > 0 && kept(t) && <span className="text-muted2"> · keeps its own {COPY_FIELDS.filter((f) => opts[f.k] && albumValue(album, f.k) && t[f.key] && t[f.key] !== albumValue(album, f.k)).map((f) => f.label.toLowerCase()).join(", ")}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-[13px]">
              {touched.length ? (
                <>
                  <b>{touched.length}</b> track{touched.length === 1 ? "" : "s"} will change
                  {count("date") ? ` · ${count("date")} get the release date` : ""}
                  {count("upc") ? ` · ${count("upc")} get the UPC` : ""}
                  {count("date") || count("upc") ? (
                    <>
                      . Those will then show as <b>Album only</b>.
                    </>
                  ) : (
                    "."
                  )}
                </>
              ) : (
                "Nothing to change with these choices."
              )}
            </div>
          </>
        )}
        <div className="flex gap-2 flex-wrap">
          <Button className="bg-brand hover:bg-brand-hover" onClick={onApply} disabled={!touched.length || busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Apply to ${touched.length} track${touched.length === 1 ? "" : "s"}`}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
